import { useState } from 'react'
import {
  Button,
  MessageBar,
  Dialog,
  DialogSurface,
  DialogBody,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@fluentui/react-components'
import { Delete16Regular } from '@fluentui/react-icons'
import { apiFetchJson } from '../lib/apiFetch'
import { SERVICE_LABELS } from '../lib/services'

/** Wipes all synced ServiceAssignments for a service (any ingestion method) — a manual reset so
 * the next sync/import starts clean, e.g. after cleaning up a duplicate account vendor-side.
 * Leaves license-type config and PersonLinks alone; only the assignment rows themselves. */
export default function ClearServiceDataButton(props: { serviceId: string; onCleared: () => void }) {
  const { serviceId, onCleared } = props
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [clearing, setClearing] = useState(false)
  const [message, setMessage] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  async function handleConfirm() {
    setClearing(true)
    setError(null)
    try {
      const res = await apiFetchJson<{ deletedCount: number }>(`/api/sync/${serviceId}`, { method: 'DELETE' })
      setMessage(`Cleared ${res.deletedCount} record${res.deletedCount === 1 ? '' : 's'}.`)
      onCleared()
    } catch (e) {
      setError(String(e))
    } finally {
      setClearing(false)
      setConfirmOpen(false)
    }
  }

  const serviceLabel = SERVICE_LABELS[serviceId] ?? serviceId

  return (
    <div>
      <Button appearance="subtle" icon={<Delete16Regular />} onClick={() => setConfirmOpen(true)}>
        Clear all {serviceLabel} data
      </Button>
      {message && (
        <MessageBar intent="success" style={{ marginTop: 16 }}>
          {message}
        </MessageBar>
      )}
      {error && (
        <MessageBar intent="error" style={{ marginTop: 16 }}>
          Error: {error}
        </MessageBar>
      )}

      <Dialog open={confirmOpen} onOpenChange={(_e, data) => !data.open && setConfirmOpen(false)}>
        <DialogSurface>
          <DialogBody>
            <DialogTitle>Clear all {serviceLabel} data?</DialogTitle>
            <DialogContent>
              This permanently deletes every synced {serviceLabel} license record. It doesn't
              touch license-type/icon config or any manual user links — only the assignment data
              itself, which the next sync or CSV import will recreate from scratch. This cannot
              be undone.
            </DialogContent>
            <DialogActions>
              <Button appearance="primary" onClick={handleConfirm} disabled={clearing}>
                {clearing ? 'Clearing...' : 'Clear data'}
              </Button>
              <Button onClick={() => setConfirmOpen(false)} disabled={clearing}>
                Cancel
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </div>
  )
}
